/**
 * SessionDetailPage — one voice session with transcript, extraction,
 * and promote-to-project action.
 *
 * The page has three panels stacked:
 *   1. Header — title, status, timestamps, project label
 *   2. Extraction — either "no summary yet" (with Summarize CTA) OR
 *      the structured lists from public.session_summaries.
 *   3. Transcript — turns rendered in reading order with F/Q avatars.
 *
 * All state is derived from a single fetch(session_id) and one fetch
 * to /api/sessions/{id}/summary. No local mock data.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ArrowLeft, Loader2, MessagesSquare, Sparkles, CheckCircle2, Lightbulb,
    ListChecks, Brain, ChevronRight,
} from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useActiveProject } from "@/context/ActiveProjectContext";
import {
    getSession, getSummary, enqueueSummarize, promoteFromSummary,
} from "@/lib/knowledge";

function StatePill({ status }) {
    const tone =
        status === "open" ? "text-qoryn-cyan bg-qoryn-cyan/10" :
        status === "ended" ? "text-qoryn-textDim bg-white/5" :
        status === "error" ? "text-red-300 bg-red-500/10" :
        "text-qoryn-textMuted bg-white/5";
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono-ui text-[9px] tracking-widest uppercase ${tone}`}>
            {status}
        </span>
    );
}

function ExtractionList({ icon: Icon, label, items, testid }) {
    if (!items || items.length === 0) return null;
    return (
        <div data-testid={testid} className="qoryn-panel rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 text-qoryn-cyan" strokeWidth={1.75} />
                <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                    {label} ({items.length})
                </div>
            </div>
            <ul className="space-y-2">
                {items.map((it, i) => (
                    <li key={i} className="font-sans text-[13px] text-qoryn-text flex items-start gap-2">
                        <ChevronRight className="h-3 w-3 mt-1 text-qoryn-textMuted flex-shrink-0" strokeWidth={1.75} />
                        <span className="flex-1">
                            {it.text}
                            {it.source_turn_indexes?.length > 0 && (
                                <span className="ml-2 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                                    turn {it.source_turn_indexes.join(", ")}
                                </span>
                            )}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function TurnRow({ turn }) {
    const isF = turn.speaker === "founder";
    return (
        <div data-testid={`turn-${turn.turn_index}`} className="flex gap-3 py-3 border-b border-white/5 last:border-0">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-mono-ui text-[10px] font-bold ${isF ? "bg-qoryn-cyan/20 text-qoryn-cyan" : "bg-white/5 text-qoryn-textDim"}`}>
                {isF ? "F" : "Q"}
            </div>
            <div className="flex-1 space-y-1 min-w-0">
                <div className="flex items-center gap-2">
                    <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                        {isF ? "Founder" : "Qoryn"} · turn {turn.turn_index}
                    </div>
                </div>
                <p className="font-sans text-[13px] text-qoryn-text leading-relaxed break-words">
                    {turn.normalized_text || turn.raw_text || <span className="italic text-qoryn-textMuted">(empty)</span>}
                </p>
            </div>
        </div>
    );
}

export default function SessionDetailPage() {
    const { id } = useParams();
    const { projects, activeId } = useActiveProject();
    const [session, setSession] = useState(null);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(null);
    const [summarizing, setSummarizing] = useState(false);
    const [promoting, setPromoting] = useState(false);

    const reload = useCallback(async () => {
        setError(null);
        try {
            const [s, sum] = await Promise.all([getSession(id), getSummary(id)]);
            setSession(s);
            setSummary(sum);
        } catch (e) {
            setError(String(e.message || e));
        }
    }, [id]);

    useEffect(() => { reload(); }, [reload]);

    const onSummarize = useCallback(async () => {
        setSummarizing(true);
        try {
            await enqueueSummarize(id);
            toast.success("Extraction queued. It usually completes in under a minute.");
            // Poll for landing.
            const deadline = Date.now() + 120_000;
            while (Date.now() < deadline) {
                await new Promise((r) => setTimeout(r, 4000));
                const sum = await getSummary(id);
                if (sum) {
                    setSummary(sum);
                    toast.success("Extraction complete.");
                    return;
                }
            }
            toast.warning("Extraction is still running. Refresh in a moment.");
        } catch (e) {
            toast.error(`Summarize failed: ${e.message}`);
        } finally {
            setSummarizing(false);
        }
    }, [id]);

    const onPromote = useCallback(async () => {
        const projectId = session?.project_id || activeId;
        if (!projectId) {
            toast.error("Choose an active project first.");
            return;
        }
        setPromoting(true);
        try {
            const r = await promoteFromSummary(id, { project_id: projectId });
            if (r.skipped_reason) {
                toast.info(r.skipped_reason);
                return;
            }
            const created = [
                r.decisions_created && `${r.decisions_created} decisions`,
                r.ideas_created && `${r.ideas_created} ideas`,
                r.tasks_created && `${r.tasks_created} tasks`,
                r.memories_created && `${r.memories_created} memories`,
            ].filter(Boolean).join(", ");
            toast.success(created ? `Promoted: ${created}` : "Nothing to promote — extraction found no items.");
        } catch (e) {
            toast.error(`Promote failed: ${e.message}`);
        } finally {
            setPromoting(false);
        }
    }, [id, session?.project_id, activeId]);

    const projectName = useMemo(
        () => (session?.project_id && projects.find((p) => p.id === session.project_id)?.name) || null,
        [session?.project_id, projects],
    );

    return (
        <div data-testid="session-detail-page" className="space-y-6">
            <Link
                to="/conversations"
                className="inline-flex items-center gap-1.5 font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim hover:text-qoryn-text transition-colors"
            >
                <ArrowLeft className="h-3 w-3" strokeWidth={1.75} /> Back to conversations
            </Link>

            {!session && !error && (
                <div className="py-10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-qoryn-cyan" /></div>
            )}
            {error && (
                <EmptyState icon={MessagesSquare} title="Couldn't load this session." body={error} />
            )}
            {session && (
                <>
                    <div className="qoryn-panel rounded-2xl p-5 space-y-3">
                        <div className="flex items-baseline gap-3 flex-wrap">
                            <h1 className="font-display text-[24px] font-light tracking-tight text-qoryn-text">
                                {session.title || "Untitled session"}
                            </h1>
                            <StatePill status={session.status} />
                        </div>
                        <div className="flex items-center gap-3 flex-wrap font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim">
                            <span data-testid="session-started-at">{new Date(session.started_at).toLocaleString()}</span>
                            {session.duration_seconds != null && (
                                <span>· {session.duration_seconds}s</span>
                            )}
                            {projectName && <span>· {projectName}</span>}
                            <span>· {session.turn_count} turn{session.turn_count === 1 ? "" : "s"}</span>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            <Button
                                data-testid="btn-summarize"
                                variant="outline"
                                size="sm"
                                onClick={onSummarize}
                                disabled={summarizing || session.turn_count === 0}
                                className="rounded-full"
                            >
                                {summarizing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                                {summary ? "Re-extract" : "Extract summary"}
                            </Button>
                            <Button
                                data-testid="btn-promote"
                                variant="outline"
                                size="sm"
                                onClick={onPromote}
                                disabled={promoting || !summary}
                                className="rounded-full"
                                title={!summary ? "Extract first" : "Promote extracted items to project"}
                            >
                                {promoting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ChevronRight className="h-3.5 w-3.5 mr-1.5" />}
                                Promote to project
                            </Button>
                        </div>
                    </div>

                    <section data-testid="extraction-panel" className="space-y-3">
                        <h2 className="font-display text-[16px] tracking-tight text-qoryn-text">Extraction</h2>
                        {!summary && (
                            <EmptyState
                                icon={Sparkles}
                                title="No summary yet."
                                body={session.turn_count === 0
                                    ? "This session has no turns to summarize."
                                    : "Click 'Extract summary' above to run the summarizer."}
                            />
                        )}
                        {summary && (
                            <div className="space-y-3">
                                {summary.summary && (
                                    <div data-testid="summary-blurb" className="qoryn-panel rounded-2xl p-5">
                                        <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim mb-2">
                                            {/* Forward-facing header: never show
                                                provider or model names. The full
                                                generator_version (e.g.
                                                openai:gpt-4o-mini:v1) is stored
                                                for audit and shown in
                                                Settings → Advanced. Here we only
                                                surface the trailing schema
                                                version (v1, v2, …) when present.
                                                Standing rule effective 2026-08-18. */}
                                            {(() => {
                                                const m = /(:|^)(v\d+)$/i.exec(
                                                    summary.generator_version ?? "",
                                                );
                                                return m ? `Summary · ${m[2].toLowerCase()}` : "Summary";
                                            })()}
                                        </div>
                                        <p className="font-sans text-[14px] text-qoryn-text leading-relaxed">
                                            {summary.summary}
                                        </p>
                                    </div>
                                )}
                                {summary.topics?.length > 0 && (
                                    <div data-testid="summary-topics" className="qoryn-panel rounded-2xl p-5">
                                        <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim mb-2">
                                            Topics
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {summary.topics.map((t, i) => (
                                                <span key={i} className="rounded-full px-2 py-0.5 font-mono-ui text-[10px] tracking-wide bg-white/5 text-qoryn-text">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <ExtractionList icon={CheckCircle2} label="Confirmed decisions" items={summary.confirmed_decisions} testid="summary-decisions" />
                                <ExtractionList icon={Lightbulb} label="Ideas & brainstorms" items={summary.brainstorms} testid="summary-brainstorms" />
                                <ExtractionList icon={Sparkles} label="Qoryn proposals" items={summary.qoryn_proposals} testid="summary-proposals" />
                                <ExtractionList icon={ListChecks} label="Action items" items={summary.action_items} testid="summary-actions" />
                                <ExtractionList icon={Brain} label="Candidate memories" items={summary.candidate_memories} testid="summary-memories" />
                                <ExtractionList icon={ChevronRight} label="Founder preferences" items={summary.founder_preferences} testid="summary-preferences" />
                                <ExtractionList icon={ChevronRight} label="Unresolved questions" items={summary.unresolved_questions} testid="summary-unresolved" />
                            </div>
                        )}
                    </section>

                    <section data-testid="transcript-panel" className="qoryn-panel rounded-2xl p-5">
                        <h2 className="font-display text-[16px] tracking-tight text-qoryn-text mb-2">
                            Transcript ({session.turns?.length || 0})
                        </h2>
                        {session.turns?.length === 0 && (
                            <p className="font-sans text-[13px] text-qoryn-textDim">
                                No turns recorded for this session.
                            </p>
                        )}
                        <div>
                            {session.turns?.map((t) => <TurnRow key={t.id} turn={t} />)}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
